import {
  AgenticMarkdown,
  infoHintText,
  MarkdownInfoHint,
} from 'designSystem/AgenticMarkdown'
import {
  remarkSubjectLinks,
  SUBJECT_LINK_PREFIX,
} from 'features/facilityModel/remarkSubjectLinks'
import {
  subjectDetailHref,
  useOpenSubjectResource,
} from 'features/facilityModel/useFacilityModelNavigation'
import { useKnowledgeDocumentUXContextOptional } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useSubjectLinkIndex } from 'features/knowledgeDocumentViewer/hooks/useSubjectLinkIndex'
import React, { useMemo } from 'react'
import type { Components, Options } from 'react-markdown'
import styled from 'styled-components'

const SubjectLink = styled.a`
  color: inherit;
  text-decoration: underline dotted;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

/**
 * Doc-chat's Markdown renderer, upgraded to turn subject-name/alias mentions
 * in response text into clickable Subject references. Falls back to the
 * plain `AgenticMarkdown` renderer whenever the subject-link index isn't
 * ready — idle, loading, error, skipped, or the viewer has no Facility Model
 * access — so chat rendering never blocks on or breaks over linkification.
 */
export const SubjectLinkedAgenticMarkdown = React.memo(
  function SubjectLinkedAgenticMarkdown({
    children,
  }: {
    children: string
  }): React.ReactElement {
    const siteId = useKnowledgeDocumentUXContextOptional()?.siteId
    const index = useSubjectLinkIndex(siteId)
    const openSubject = useOpenSubjectResource(siteId ?? null)

    const remarkPlugins: Options['remarkPlugins'] = useMemo(
      () => (index === undefined ? [] : [[remarkSubjectLinks, { index }]]),
      [index]
    )

    const components: Components | undefined = useMemo(() => {
      if (index === undefined) return undefined
      return {
        a: ({ href, title, children: linkChildren }) => {
          const hint = infoHintText(href, title)
          if (hint) return <MarkdownInfoHint hint={hint} />
          if (!href?.startsWith(SUBJECT_LINK_PREFIX)) {
            return <a href={href}>{linkChildren}</a>
          }
          const uid = decodeURIComponent(href.slice(SUBJECT_LINK_PREFIX.length))
          return (
            <SubjectLink
              href={subjectDetailHref(uid, siteId ?? null)}
              title={title ?? undefined}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                openSubject(uid, title ?? String(linkChildren))
              }}>
              {linkChildren}
            </SubjectLink>
          )
        },
      }
    }, [index, openSubject, siteId])

    if (index === undefined) {
      return <AgenticMarkdown>{children}</AgenticMarkdown>
    }

    return (
      <AgenticMarkdown remarkPlugins={remarkPlugins} components={components}>
        {children}
      </AgenticMarkdown>
    )
  }
)
